import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../lib/constants";
import { SEO, SITE_URL } from "../../components/SEO";
import { MarkdownLite } from "../../components/guides/MarkdownLite";
import { Breadcrumbs, breadcrumbLd, GuideMeta, GuideFaq, GuideSources, RelatedGuides, GuideCta } from "../../components/guides/GuideParts";
import NotFound from "../NotFound";

const CATEGORY_NAMES = {
  "declaration-fiscale-frontalier-suisse": "Déclaration fiscale frontalier Suisse", "frontaliers-geneve": "Frontaliers Genève",
  "impot-a-la-source-geneve": "Impôt à la source Genève", "tou-geneve": "TOU Genève", "dris-rectification": "DRIS / rectification",
  "quasi-resident": "Quasi-résident", "formulaires-fiscaux": "Formulaires fiscaux", "deductions-fiscales": "Déductions fiscales", "actualites-fiscales": "Actualités fiscales",
};

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function GuidePage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    axios.get(`${API}/guides/${slug}`)
      .then(({ data }) => { setGuide(data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "notfound") return <NotFound />;
  if (status === "loading" || !guide) {
    return <main className="pt-[76px] min-h-[60vh]"><div className="max-w-4xl mx-auto px-5 py-24 animate-pulse space-y-4"><div className="h-4 w-1/3 bg-[#E5E7EB] rounded" /><div className="h-12 w-3/4 bg-[#E5E7EB] rounded" /><div className="h-4 w-full bg-[#E5E7EB] rounded" /></div></main>;
  }

  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: "Guides fiscaux frontaliers", to: "/guides-fiscaux-frontaliers" },
    { label: CATEGORY_NAMES[guide.category] || guide.category, to: `/guides-fiscaux-frontaliers/${guide.category}` },
    { label: guide.title, to: `/${guide.slug}` },
  ];
  const words = [guide.intro, ...(guide.sections || []).map((s) => s.body)].join(" ").split(/\s+/).length;
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Article", headline: guide.title, description: guide.meta_description,
      author: { "@type": "Organization", name: guide.author, url: SITE_URL }, publisher: { "@type": "Organization", name: "Fiscoconseils", logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon-512.png` } },
      datePublished: guide.published_at, dateModified: guide.updated_at, mainEntityOfPage: `${SITE_URL}/${guide.slug}`, inLanguage: "fr-FR", keywords: guide.keyword, wordCount: words,
    },
    breadcrumbLd(crumbs),
    guide.faq?.length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: guide.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) } : null,
  ];

  return (
    <main data-testid="guide-page" className="pt-[76px]">
      <SEO title={guide.meta_title} description={guide.meta_description} path={`/${guide.slug}`} noindex={guide.index === false} type="article" jsonLd={jsonLd} />
      <article>
        <header className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-10 pb-14">
            <Breadcrumbs items={crumbs} />
            <Link to={`/guides-fiscaux-frontaliers/${guide.category}`} className="inline-block mt-8 text-[11px] tracking-[0.2em] uppercase font-bold text-gold hover:underline underline-offset-4">{CATEGORY_NAMES[guide.category] || guide.category}</Link>
            <h1 data-testid="guide-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A1128] leading-[1.05] mt-3">{guide.title}</h1>
            <p data-testid="guide-intro" className="text-base md:text-lg text-[#4B5563] mt-6 leading-relaxed">{guide.intro}</p>
            <div className="mt-6"><GuideMeta g={guide} /></div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-14">
          {guide.sections?.length > 3 && (
            <nav data-testid="guide-toc" aria-label="Sommaire" className="bg-white border border-[#C4C8CC]/50 rounded-2xl p-6 mb-12">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280]">Sommaire</p>
              <ol className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5">
                {guide.sections.map((s, i) => (
                  <li key={s.h2}><a href={`#${slugify(s.h2)}`} className="text-sm text-[#374151] hover:text-[#0A1128] inline-flex gap-2"><span className="text-[#9CA3AF]">{i + 1}.</span>{s.h2}</a></li>
                ))}
              </ol>
            </nav>
          )}

          <div className="space-y-12">
            {(guide.sections || []).map((s) => (
              <section key={s.h2} id={slugify(s.h2)} className="scroll-mt-24">
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A1128] mb-5">{s.h2}</h2>
                <MarkdownLite text={s.body} />
              </section>
            ))}
          </div>

          <GuideFaq faq={guide.faq} />
          <GuideCta />
          <GuideSources sources={guide.sources} disclaimer={guide.disclaimer} />
          <RelatedGuides guides={guide.related_guides} />
        </div>
      </article>
    </main>
  );
}
